import React, { useContext, useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from 'react-query';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AlertMessage } from '@eposnow/ui-core';
import AddressForm from './AddressForm';
import { UserContext } from '../../context/UserContext';
import { UIContext } from '../../context/UIContext';
import { locations2ValidationSchema } from './validationSchemas';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import DrawerTitlebar from '../../components/DrawerTitlebar';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import { LocationV1, LocationV4 } from '../../types';
import { checkAndFormatCountry, locationV1toV4 } from '../../helpers/helpers';
import useCountries from '../../helpers/useCountries';

const emptyAddress = {
  id: 0,
  name: '',
  description: '',
  countryName: '',
  organisationId: '',
  address1: '',
  address2: '',
  town: '',
  county: '',
  postCode: '',
  emailAddress: '',
  phoneNumber: '',
};

const CreateEditAddressCard = ({
  currentAddress,
  resetAddress,
  setSuccessAddressMessage,
  errorAddressMessage,
  setErrorAddressMessage,
  mode,
}: {
  currentAddress: LocationV1 | undefined;
  resetAddress: () => void;
  setSuccessAddressMessage: (message: string) => void;
  errorAddressMessage: string;
  setErrorAddressMessage: (message: string) => void;
  mode: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { apiFetch } = useContext(ApiContext);
  const { locale } = useContext(UserContext);
  const { countries } = useCountries();
  const { iOS } = useContext(UIContext);
  const queryClient = useQueryClient();
  const [initialAddress, setInitialAddress] = useState<LocationV1>(emptyAddress);
  const [addressId, setAddressId] = useState<number>(0);

  const removeSavedAddress = () => {
    const savedAddresses = JSON.parse(sessionStorage.getItem('addresses') || '{}');
    delete savedAddresses[`${addressId}`];
    sessionStorage.setItem('addresses', JSON.stringify(savedAddresses));
  };

  useEffect(() => {
    const savedAddresses = JSON.parse(sessionStorage.getItem('addresses') || '{}');
    const newAddressId = mode === 'create' ? 0 : currentAddress?.id;
    setAddressId(newAddressId);
    const savedAddress = savedAddresses[`${newAddressId}`];

    let newInitialObject: LocationV1 = { ...emptyAddress, id: 0, ...savedAddress };
    if (mode === 'create')
      sessionStorage.setItem(
        'addresses',
        JSON.stringify({
          ...savedAddresses,
          [`${newAddressId}`]: newInitialObject,
        })
      );
    else {
      newInitialObject = {
        ...currentAddress,
        name: currentAddress?.name || '',
        description: currentAddress?.description || '',
        address1: currentAddress?.address1 || '',
        phoneNumber: currentAddress?.phoneNumber || '',
        address2: currentAddress?.address2 || '',
        town: currentAddress?.town || '',
        county: currentAddress?.county || '',
        postCode: currentAddress?.postCode || '',
        countryName: currentAddress?.countryName || '',
        emailAddress: currentAddress?.emailAddress || '',
      };
    }

    setInitialAddress(newInitialObject);
  }, [currentAddress]);

  const updateOrCreateAddressapiFunction = (address: LocationV4) =>
    apiFetch(
      `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location`,
      address,
      mode === 'create' ? 'POST' : 'PUT'
    );

  const { isLoading, mutate: updateAddresses } = useMutation(
    (address: LocationV4) => updateOrCreateAddressapiFunction(address),
    {
      async onSuccess() {
        await queryClient.invalidateQueries('addresses');
        resetAddress();
        removeSavedAddress();

        setSuccessAddressMessage(
          t(
            `screens.locations2.${
              mode === 'create' ? 'successCreateAddress' : 'successEditAddress'
            }`
          )
        );
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error creating/updating address'), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorAddressMessage(
          t(`errors.${mode === 'create' ? 'errorCreatingAddress' : 'errorUpdatingAddress'}`)
        );
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      id: initialAddress?.id,
      name: initialAddress?.name,
      description: initialAddress?.description,
      country: initialAddress?.countryName,
      organisationId: initialAddress?.organisationId,
      addressLine1: initialAddress?.address1,
      address2: initialAddress?.address2,
      town: initialAddress?.town,
      county: initialAddress?.county,
      postCode: initialAddress?.postCode,
      emailAddress: initialAddress?.emailAddress,
      phoneNumber: initialAddress?.phoneNumber,
    },
    validationSchema: locations2ValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoading) return;
      const requestValues = {
        ...values,
        address1: values.addressLine1,
        countryName: checkAndFormatCountry(values.country, countries),
      } as LocationV1;
      actions.setSubmitting(false);

      const locationV4Format = locationV1toV4(requestValues);
      updateAddresses({
        ...locationV4Format,
      });
    },
  });

  return (
    <Box
      component="div"
      sx={{
        overflow: 'auto',
        ...(!iOS && {
          marginBottom: '65px',
        }),
      }}
    >
      <DrawerTitlebar
        title={t(`screens.locations2.${mode === 'create' ? 'addNewAddress' : 'editAddress'}`)}
        icon={mode === 'create' ? <AddIcon /> : <EditOutlinedIcon />}
        onClose={resetAddress}
      />
      {errorAddressMessage && (
        <AlertMessage
          type="error"
          styles={{ marginTop: 1.5 }}
          action={() => setErrorAddressMessage('')}
          iconAlignment="baseline"
          locale={locale}
          theme={theme}
        >
          {errorAddressMessage}
        </AlertMessage>
      )}
      <Box sx={{ padding: 2, paddingTop: 0 }}>
        <AddressForm
          formik={formik}
          cancelAction={() => {
            resetAddress();
            removeSavedAddress();
          }}
          isSubmitting={isLoading}
        />
      </Box>
    </Box>
  );
};

export default CreateEditAddressCard;
